/**
 * Pure helpers for the user-group Departments tab and for narrowing employee
 * department pickers to the signed-in user's department scope.
 *
 * Contract with the backend (`PUT /user-groups/:id`, `GET /user-groups`, and
 * `/me`):
 * - `departments?: { companyId: string; departmentIds: number[] }[]` on a
 *   group — a company left out of the array means "All departments"; a
 *   listed company means "Selected", with at least one department.
 * - `/me` exposes `departmentScope?: Record<companyId, number[]>` (present
 *   only for a department-restricted caller) and `fullDepartmentCompanyIds?`
 *   (the companies where that caller holds every department).
 */

export type CompanyDepartmentOption = {
  id: number;
  departmentName: string;
};

/** companyId -> the department ids the viewer is restricted to there. */
export type DepartmentScope = Record<string, number[]>;

export type GroupCompanyDepartments = {
  companyId: string;
  departmentIds: number[];
};

export type DepartmentTabRow = {
  companyId: string;
  companyName: string;
  mode: 'all' | 'selected';
  departmentIds: number[];
  departments: CompanyDepartmentOption[];
  /** Outside the editor's own write scope: shown, never saved. */
  readOnly: boolean;
};

/**
 * Builds the `PUT /user-groups/:id` `departments` payload from the tab's
 * rows. A row on "All" is omitted — the contract treats an unlisted company
 * as All departments. Read-only rows, and any row outside
 * `editableCompanyIds`, are always dropped: the backend only replaces rows
 * for companies in the caller's own write scope, so sending them would
 * silently do nothing and must not be presented as saved.
 */
export function toGroupDepartmentsPayload(
  rows: DepartmentTabRow[],
  editableCompanyIds: string[] | null,
): GroupCompanyDepartments[] {
  return rows
    .filter((row) => !row.readOnly)
    .filter(
      (row) =>
        editableCompanyIds === null ||
        editableCompanyIds.includes(row.companyId),
    )
    .filter((row) => row.mode === 'selected' && row.departmentIds.length > 0)
    .map((row) => ({
      companyId: row.companyId,
      departmentIds: [...row.departmentIds],
    }));
}

/**
 * The department options an employee dropdown should offer for a company,
 * narrowed to the viewer's department scope when they are restricted there.
 */
export function departmentOptionsFor(
  company:
    | { id: string; departments?: CompanyDepartmentOption[] | null }
    | null
    | undefined,
  departmentScope?: DepartmentScope | null,
): CompanyDepartmentOption[] {
  const all = company?.departments ?? [];
  const allowed = company?.id ? departmentScope?.[company.id] : undefined;
  if (!allowed) return all;
  const allowedIds = new Set(allowed);
  return all.filter((department) => allowedIds.has(department.id));
}

/**
 * Whether "no department" is a valid choice for this company. An employee
 * with no department is visible only to a user with All departments there,
 * so a restricted user must not be offered the empty option either.
 */
export function allowsNoDepartment(
  companyId: string | null | undefined,
  departmentScope?: DepartmentScope | null,
): boolean {
  if (!departmentScope || !companyId) return true;
  return !(companyId in departmentScope);
}

/**
 * Whether the user holds every department in at least one company.
 * Company-level screens (RTI, HMRC, bank files…) need that, because they are
 * never filtered down to a subset of departments.
 *
 * `fullDepartmentCompanyIds` is absent for root, admin and unrestricted
 * users (never narrowed), and is `['']` — one empty-string entry, not an
 * empty array — for a restricted user with no all-department company left.
 */
export function hasAllDepartmentCompany(
  user?: { fullDepartmentCompanyIds?: string[] | null } | null,
): boolean {
  const ids = user?.fullDepartmentCompanyIds;
  if (ids === undefined || ids === null) return true; // unrestricted, admin or root
  return ids.some((id) => id !== '');
}
